import { useCallback, useMemo, useRef, useState } from 'react';

import { useAppState, useFunctionPolling, useIsFocused } from '@garnish/core';
import { type RequestPolicy } from 'urql';

import { OrderType } from '@sg/graphql-schema';

import { getOrderTimeRange, ignoreTimezone } from '@order/utils';

import { useIsLoggedIn } from '../../context/AuthMachine';
import {
  checkIfExpiredOrder,
  checkIfLateOrder,
  checkIfPastOrder,
  checkIfPendingOrder,
  getOrderTime,
} from '../../context/OrderStatus';
import { useLastOrderStatusRefreshContext } from './hooks';
import {
  useLastOrderStatusQuery,
  type LastOrderStatusQuery,
} from './LastOrderStatus.query.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useLastOrderStatus = (params: Params) => {
  const { requestPolicy, withPolling = false, placedOrderId } = params;

  const isScreenFocused = useIsFocused();
  const isLoggedIn = useIsLoggedIn();
  const appState = useAppState({ pause: !withPolling });

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const hasLastFetchFailedRef = useRef(false);

  // ─── State ─────────────────────────────────────────────────────────────────

  const [pollCount, setPollCount] = useState(0);

  // ─── Order Status Query ───────────────────────────────────────────────────

  const isVisible = withPolling && isScreenFocused && appState === 'active';

  const refreshContext = useLastOrderStatusRefreshContext({
    refreshInterval: REFRESH_INTERVAL,
    isVisible,
    isLoggedIn,
    placedOrderId,
    pollCount,
    hasLastFetchFailed: hasLastFetchFailedRef.current,
  });

  // Not paused on blur, since unpausing always refetches and would bypass the refresh window.
  const [response] = useLastOrderStatusQuery({
    requestPolicy,
    pause: !isLoggedIn,
    context: refreshContext,
  });

  // Read by the refresh context on the next render, since it has to run before the query.
  hasLastFetchFailedRef.current = Boolean(response.error);

  const { data: status, fetching: isLoadingLastOrder } = response;

  // ─── Derived ───────────────────────────────────────────────────────────────

  const lastOrder = useMemo(() => {
    if (!isOrderStatus(status?.orderStatus)) {
      return {
        orderId: null,
        orderType: OrderType.Pickup,
        orderStatus: null,
        orderTime: null,
        hasEarnedLoyaltyPoints: false,
        isRecent: false,
        isLate: false,
        isPendingFeedback: false,
        isTrackable: false,
        isLoadingLastOrder,
        restaurantEntityId: '',
        lineItems: [],
      };
    }

    const lastOrder = status.orderStatus;

    // ─── Recent ─────────────────────────────────────────────────────────────

    const isRecent = !checkIfExpiredOrder({
      order: lastOrder.order,
      hoursInThePast: 3,
    });

    // ─── Late ───────────────────────────────────────────────────────────────

    const isLate = checkIfLateOrder(lastOrder);

    // ─── Pending Feedback ───────────────────────────────────────────────────

    const isLastOrderCompleted = checkIfPastOrder({
      order: lastOrder.order,
      flattenedOrderStatus: lastOrder.flattenedOrderStatus,
    });
    const isLastOrderExpired = checkIfExpiredOrder(lastOrder);
    const isRated = !lastOrder.order.isPendingFeedback;
    const isPendingFeedback = !isLastOrderExpired && isLastOrderCompleted && !isRated;

    // ─── Trackable ──────────────────────────────────────────────────────────

    const isTrackable = Boolean(lastOrder.order.canTrackOrderStatus);

    // ─── Last Order Info ────────────────────────────────────────────────────

    const orderId = lastOrder.order.id;
    const { orderType, hasEarnedLoyaltyPoints } = lastOrder.order;
    const orderStatus = lastOrder.flattenedOrderStatus;
    const timeWithTimezone = getOrderTime(lastOrder.order);
    const orderTime = timeWithTimezone ? ignoreTimezone(timeWithTimezone) : null;
    const { startTime = '', endTime = '' } = orderTime ? getOrderTimeRange(orderTime) : {};
    const restaurantEntityId = String(lastOrder.order.restaurant?.entityId ?? '');
    const lineItems = lastOrder.order.lineItems;

    return {
      orderId,
      orderType,
      orderStatus,
      orderTime,
      startTime,
      endTime,
      hasEarnedLoyaltyPoints,
      isRecent,
      isLate,
      isPendingFeedback,
      isRated,
      isTrackable,
      isLoadingLastOrder,
      restaurantEntityId,
      lineItems,
    };
  }, [isLoadingLastOrder, status]);

  // ───────────────────────────────────────────────────────────────────────────

  const hasPendingOrder = lastOrder.orderStatus
    ? checkIfPendingOrder(lastOrder.orderStatus)
    : false;

  const shouldSkipPolling = !isVisible || !isLoggedIn || !hasPendingOrder;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handlePoll = useCallback(() => {
    setPollCount(incrementCount);
  }, []);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useFunctionPolling(REFRESH_INTERVAL, handlePoll, shouldSkipPolling);

  // ───────────────────────────────────────────────────────────────────────────

  return lastOrder;
};

// ─── Guards ──────────────────────────────────────────────────────────────────

const isOrderStatus = (
  orderStatus: LastOrderStatusQuery['orderStatus'] | undefined,
): orderStatus is Extract<LastOrderStatusQuery['orderStatus'], { __typename: 'OrderStatus' }> =>
  orderStatus != null && '__typename' in orderStatus && orderStatus.__typename === 'OrderStatus';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function incrementCount(count: number) {
  return count + 1;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const REFRESH_INTERVAL = 1000 * 45;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  requestPolicy: RequestPolicy;
  withPolling?: boolean;
  placedOrderId?: string;
};
